import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useApi from '@/hooks/useApi';
import { toDayjsLocale } from '@/utils/dayjsLocale';
import IcCheck from '@/assets/icons/Check.svg?react';
import Row from './Row';
import SkeletonRow from './SkeletonRow';
import styles from './AwaitingSignatures.module.scss';

dayjs.extend(relativeTime);

const SKELETON_ROW_COUNT = 3;
const AWAITING_POLL_INTERVAL_MS = 10_000;

const AwaitingSignatures = () => {
  const { t, i18n } = useTranslation(['common']);
  const [getAwaiting, { response, loading }] = useApi('signings.awaiting');

  useEffect(() => {
    void getAwaiting();
    const intervalId = setInterval(
      () => void getAwaiting(),
      AWAITING_POLL_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [getAwaiting]);

  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
  });
  const dayjsLocale = toDayjsLocale(i18n.language);

  const items = response?.data ?? [];
  const isLoading = loading === true && !response;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <h2 className={styles.heading}>
          {t('common:dashboard.awaiting.title')}
        </h2>
      </header>
      {isEmpty ? (
        <div className={styles.emptyCard}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <IcCheck />
          </span>
          <div className={styles.emptyMeta}>
            <div className={styles.emptyTitle}>
              {t('common:dashboard.awaiting.emptyTitle')}
            </div>
            <div className={styles.emptySubtitle}>
              {t('common:dashboard.awaiting.emptySubtitle')}
            </div>
          </div>
        </div>
      ) : (
        <ul className={styles.list}>
          {isLoading
            ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                <SkeletonRow key={index} />
              ))
            : items.map((item) => (
                <Row
                  key={item.id}
                  item={item}
                  dateFormatter={dateFormatter}
                  dayjsLocale={dayjsLocale}
                  t={t}
                />
              ))}
        </ul>
      )}
    </section>
  );
};

export default AwaitingSignatures;
